import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { HexColorPicker } from 'react-colorful';

// Types
import { Props, BudgetCategoryManagement } from './types';

// Schema
import { BudgetCategoryManagementSchema } from './schema';

// Styles
import {
  Container,
  InputsContainer,
  ColorInputContainer,
  ColorPreview,
  ColorPickerContainer,
  AddBudgetCategoryButton,
} from './styles';

// Components
import InputText from '../../inputs/InputText';
import { Typography } from '../../display/Typography';

// Utils
import { useCacheFormInSessionStorage } from '../../../hooks/useCacheForm';

export const AddBudgetCategoryForm = ({
  onFormSubmit,
  namingPersistForm,
  initialData,
}: Props) => {
  const { t } = useTranslation(['forms', 'pages']);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [categoryErrorMessage, setCategoryErrorMessage] = useState<
    string | null
  >(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const colorFieldRef = useRef<HTMLDivElement>(null);
  const [openPickerToLeft, setOpenPickerToLeft] = useState(false);

  const {
    handleSubmit,
    control,
    register,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm<BudgetCategoryManagement>({
    defaultValues: {
      ...initialData,
      isActive: true,
      isCreated: true,
    },
    resolver: yupResolver(BudgetCategoryManagementSchema) as any,
  });

  useCacheFormInSessionStorage(namingPersistForm, control);

  const selectedColor = watch('color');

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target as Node)
      ) {
        setShowColorPicker(false);
      }
    };

    if (showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColorPicker]);

  // Decide hacia qué lado se abre la paleta para que no se salga de la
  // pantalla: por defecto hacia la derecha, y hacia la izquierda cuando no
  // cabe (pero solo si por la izquierda sí entra, que si no la cortaríamos por
  // el otro lado). Se recalcula al redimensionar, igual que DropDownList.
  useLayoutEffect(() => {
    if (!showColorPicker) return;

    const compute = () => {
      const anchor = colorFieldRef.current;
      const picker = colorPickerRef.current;
      if (!anchor || !picker) return;

      const { left, right } = anchor.getBoundingClientRect();
      const pickerWidth = picker.offsetWidth;
      const margin = 8;
      const overflowsRight = left + pickerWidth > window.innerWidth - margin;
      const fitsToLeft = right - pickerWidth >= margin;

      setOpenPickerToLeft(overflowsRight && fitsToLeft);
    };

    compute();
    // Segunda pasada tras montar la paleta, para medirla ya renderizada.
    const raf = requestAnimationFrame(compute);
    window.addEventListener('resize', compute);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', compute);
    };
  }, [showColorPicker]);

  const onSubmit = async (data: BudgetCategoryManagement) => {
    setCategoryErrorMessage(null);

    const errorMessage = await onFormSubmit(data);

    if (errorMessage) {
      setCategoryErrorMessage(errorMessage);
    } else {
      reset({
        code: '',
        name: '',
        color: '',
      });
      setShowColorPicker(false);
      setCategoryErrorMessage(null);
    }
  };

  const handleColorChange = (color: string) => {
    setValue('color', color);
  };

  return (
    <Container onSubmit={handleSubmit(onSubmit)}>
      <Typography size="L" variant="semiBold">
        {t('pages:management.addBudgetCategory')}
      </Typography>

      <InputsContainer>
        <InputText
          {...register('code')}
          label={t('common:labels.code')}
          typeInput={'normal'}
          inputSize="XS"
          placeholder={t('placeholders.budgetCategoryCode')}
          borderError={!!errors.code?.message}
        />

        <InputText
          {...register('name')}
          label={t('common:labels.name')}
          typeInput={'normal'}
          inputSize="M"
          placeholder={t('placeholders.budgetCategoryName')}
          borderError={!!errors.name?.message}
        />

        <ColorInputContainer ref={colorFieldRef}>
          <Typography size="XS" variant="semiBold">
            {t('tables:headers.color')}
          </Typography>
          <ColorPreview
            color={selectedColor}
            onClick={() => setShowColorPicker(!showColorPicker)}
            title="Seleccionar color"
          />
          {showColorPicker && (
            <ColorPickerContainer
              ref={colorPickerRef}
              $openToLeft={openPickerToLeft}
            >
              <HexColorPicker
                color={selectedColor}
                onChange={handleColorChange}
              />
            </ColorPickerContainer>
          )}
        </ColorInputContainer>

        <AddBudgetCategoryButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? '...' : '⮕'}
        </AddBudgetCategoryButton>

        {categoryErrorMessage && (
          <Typography colorText="lightCoralRed" size="XS">
            {categoryErrorMessage}
          </Typography>
        )}
      </InputsContainer>
    </Container>
  );
};

export default AddBudgetCategoryForm;
